import { firebase, defaultFirebaseConfig } from './firebase-config';
import { encryptData, decryptData } from './encrypt';
import 'firebase/messaging';
import { notificationsApi } from '../api/notifications';

let isRequestingFCM = false;
const FCM_WORKER_PATH = '/firebase-messaging-sw.js';
const FCM_WORKER_SCOPE = '/firebase-cloud-messaging-push-scope/';

export const requestNotificationPermission = async (userId) => {
  if (isRequestingFCM) return;
  isRequestingFCM = true;

  try {
    if (!firebase.messaging.isSupported()) {
      console.log('هذا المتصفح لا يدعم الإشعارات.');
      return;
    }

    // Cleanup old SW registrations that may still handle push and create duplicates.
    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const reg of regs) {
          const activeUrl = reg.active?.scriptURL || '';
          const waitingUrl = reg.waiting?.scriptURL || '';
          const installingUrl = reg.installing?.scriptURL || '';
          const combined = `${activeUrl} ${waitingUrl} ${installingUrl}`;
          const isLegacyPushSw = combined.includes('/pwabuilder-sw.js');
          const isFcmSw = combined.includes('/firebase-messaging-sw.js');
          const isRootScopedFcmSw = isFcmSw && new URL(reg.scope).pathname === '/';
          if ((isLegacyPushSw && !isFcmSw) || isRootScopedFcmSw) {
            await reg.unregister();
            console.log('Unregistered legacy service worker to avoid duplicate push notifications.');
          }
        }
      }
    } catch (cleanupErr) {
      console.warn('Failed to cleanup legacy service workers:', cleanupErr);
    }

    let permission = 'granted';
    if ('Notification' in window) {
      permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission();
    }

    if (permission !== 'granted') {
      console.log('لم يتم منح الإذن للإشعارات.');
      return;
    }

    const registration = await navigator.serviceWorker.register(FCM_WORKER_PATH, {
      scope: FCM_WORKER_SCOPE,
    });

    const messaging = firebase.messaging();

    // ── احصل على التوكن الحالي من FCM ────────────────────────────────────
    const currentToken = await messaging.getToken({
      vapidKey: defaultFirebaseConfig.vapidKey,
      
      serviceWorkerRegistration: registration
    });

    if (!currentToken) {
      console.warn('لم يتم الحصول على توكن FCM.');
      return;
    }

    // ── المقارنة مع التوكن المحفوظ محلياً ──────────────────────────────
    const savedEncrypted = localStorage.getItem('fcm_token_value');
    const savedToken     = savedEncrypted ? decryptData(savedEncrypted) : null;
    const tokenChanged   = savedToken !== currentToken;
    const ownerKey = 'fcm_token_owner';
    const savedOwner = localStorage.getItem(ownerKey);

    if (!tokenChanged && savedOwner === userId && localStorage.getItem(`fcm_setup_${userId}`) === 'true') {
      // التوكن صالح وموجود في DB للمستخدم الحالي — لا حاجة لتحديث
      console.log('التوكن لا يزال صالحاً للمستخدم الحالي.');
      return;
    }

    // FCM remains only as the browser push transport. Token ownership is
    // always registered with Spring/Gorush, never Realtime Database.
    const previousDeviceId = localStorage.getItem('amposter_push_device_id');
    if (previousDeviceId && (tokenChanged || (savedOwner && savedOwner !== userId))) {
      await notificationsApi.removeDevice(previousDeviceId).catch(() => {});
    }
    const device = await notificationsApi.registerDevice({
      token: currentToken,
      platform: 'WEB',
      deviceName: (navigator.userAgent || 'Amposter PWA').slice(0, 120),
    });
    if (device?.id) localStorage.setItem('amposter_push_device_id', device.id);
    localStorage.setItem(`fcm_setup_${userId}`, 'true');
    localStorage.setItem('fcm_token_value', encryptData(currentToken));
    localStorage.setItem(ownerKey, userId);

    console.log('تم تحديث توكن الإشعارات بنجاح وربطه بالمستخدم الحالي.');

  } catch (error) {
    console.error('خطأ أثناء تفعيل الإشعارات:', error);
    // امسح الكاش المحلي حتى تُعاد المحاولة في الزيارة القادمة
    localStorage.removeItem(`fcm_setup_${userId}`);
    localStorage.removeItem('fcm_token_value');
    localStorage.removeItem('fcm_token_owner');
  } finally {
    isRequestingFCM = false;
  }
};

export const removeFCMToken = async (userId) => {
  try {
    const encryptedToken = localStorage.getItem('fcm_token_value');
    if (encryptedToken) decryptData(encryptedToken);

    const deviceId = localStorage.getItem('amposter_push_device_id');
    if (deviceId) await notificationsApi.removeDevice(deviceId).catch(() => {});
    localStorage.removeItem('amposter_push_device_id');

    if (firebase.messaging.isSupported()) {
      const messaging = firebase.messaging();
      await messaging.deleteToken();
    }

    localStorage.removeItem(`fcm_setup_${userId}`);
    localStorage.removeItem('fcm_token_value');
    localStorage.removeItem('fcm_token_owner');

  } catch (error) {
    console.error('خطأ أثناء حذف توكن الإشعارات:', error);
  }
};
